'use client';

import { useState } from 'react';
import {
  Badge,
  BodyDefault,
  MetaDefault,
  MonoSmall,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import type {
  CoPilotContextViewCompactionEntryDebug,
  CoPilotContextViewDebugResponse,
  CoPilotContextViewMessageDebug,
} from '@terra/shared-dto-chat-types';
import { useTranslations } from 'next-intl';
import { formatTokenCount } from '../../token-usage/tokenUsageEstimate';

const ROLE_COLORS: Record<string, string> = {
  ai: 'var(--colors-blue-9)',
  human: 'var(--colors-green-9)',
  system: 'var(--colors-purple-9)',
  tool: 'var(--colors-orange-9)',
  unknown: 'var(--colors-gray-9)',
};

const contentToText = (
  content: CoPilotContextViewMessageDebug['content'],
): string => {
  if (typeof content === 'string') {
    return content;
  }

  return content
    .map((block) => block.text ?? `[${block.type}]`)
    .join('\n')
    .trim();
};

const describeToolCalls = (
  message: CoPilotContextViewMessageDebug,
): string | null => {
  if (!message.toolCalls || message.toolCalls.length === 0) {
    return null;
  }

  return message.toolCalls
    .map((toolCall) => toolCall.name ?? 'tool')
    .join(', ');
};

function ContextViewMessageRow({
  message,
  summaryBadgeLabel,
  tokenCountLabel,
}: {
  message: CoPilotContextViewMessageDebug;
  summaryBadgeLabel: string;
  tokenCountLabel: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const text = contentToText(message.content);
  const toolCallNames = describeToolCalls(message);
  const roleColor = ROLE_COLORS[message.messageType] ?? ROLE_COLORS.unknown;

  return (
    <Box
      px={3}
      py={2}
      borderWidth="1px"
      borderColor={
        message.isCompactionSummary ? 'var(--colors-blue-7)' : 'border.subtle'
      }
      borderRadius="md"
      bg={message.isCompactionSummary ? 'bg.subtle' : 'bg.default'}
      cursor="pointer"
      onClick={() => setExpanded((current) => !current)}
    >
      <HStack justifyContent="space-between" alignItems="center" gap={2}>
        <HStack gap={2} alignItems="center" minW={0}>
          <Badge
            variant="outline"
            size="sm"
            color={roleColor}
            borderColor={roleColor}
          >
            {message.isCompactionSummary
              ? summaryBadgeLabel
              : message.messageType}
          </Badge>
          {message.name && (
            <MonoSmall color="fg.muted" truncate>
              {message.name}
            </MonoSmall>
          )}
          {toolCallNames && (
            <MonoSmall color="fg.muted" truncate>
              → {toolCallNames}
            </MonoSmall>
          )}
        </HStack>
        <MonoSmall color="fg.muted" flexShrink={0}>
          {tokenCountLabel}
        </MonoSmall>
      </HStack>
      {text.length > 0 && (
        <BodyDefault
          display="block"
          color="fg.subtle"
          whiteSpace="pre-wrap"
          mt={1.5}
          maxHeight={expanded ? undefined : '60px'}
          overflow="hidden"
        >
          {text}
        </BodyDefault>
      )}
    </Box>
  );
}

function CompactionEntryRow({
  entry,
  compactionBadgeLabel,
  tokenRangeLabel,
}: {
  entry: CoPilotContextViewCompactionEntryDebug;
  compactionBadgeLabel: string;
  tokenRangeLabel: string;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box
      px={3}
      py={2}
      borderWidth="1px"
      borderColor="var(--colors-blue-7)"
      borderRadius="md"
      bg="bg.subtle"
      cursor="pointer"
      onClick={() => setExpanded((current) => !current)}
    >
      <HStack justifyContent="space-between" alignItems="center" gap={2}>
        <HStack gap={2} alignItems="center" minW={0}>
          <Badge
            variant="outline"
            size="sm"
            color="var(--colors-blue-9)"
            borderColor="var(--colors-blue-9)"
          >
            {compactionBadgeLabel}
          </Badge>
          <MonoSmall color="fg.muted" truncate>
            {new Date(entry.createdAt).toLocaleTimeString()}
          </MonoSmall>
        </HStack>
        <MonoSmall color="fg.muted" flexShrink={0}>
          {tokenRangeLabel}
        </MonoSmall>
      </HStack>
      <BodyDefault
        display="block"
        color="fg.subtle"
        whiteSpace="pre-wrap"
        mt={1.5}
        maxHeight={expanded ? undefined : '60px'}
        overflow="hidden"
      >
        {entry.summary}
      </BodyDefault>
    </Box>
  );
}

export function ContextViewHeaderStats({
  data,
}: {
  data: CoPilotContextViewDebugResponse;
}) {
  const t = useTranslations('chat.copilot.agentContext');
  const usedPercent = Math.min(Math.round(data.usedFraction * 100), 999);
  const isOverTrigger = data.usedTokens >= data.triggerTokens;
  const barFraction = Math.min(data.usedFraction, 1);

  return (
    <VStack alignItems="stretch" gap={1} minW="260px">
      <HStack justifyContent="space-between" alignItems="center" gap={3}>
        <MetaDefault color="fg.muted" fontFamily="mono">
          {t('tokenStats', {
            percent: usedPercent,
            used: formatTokenCount(data.usedTokens),
            window: formatTokenCount(data.effectiveWindowTokens),
          })}
        </MetaDefault>
        <MetaDefault color="fg.muted" fontFamily="mono">
          {t('compactionClearedStats', {
            cleared: data.clearedToolResultCount,
            compacted: data.compactionEntries.length,
          })}
        </MetaDefault>
      </HStack>
      <Box
        h="4px"
        borderRadius="full"
        bg="bg.subtle"
        overflow="hidden"
        position="relative"
      >
        <Box
          h="full"
          borderRadius="full"
          bg={isOverTrigger ? 'var(--colors-fg-error)' : 'var(--colors-blue-9)'}
          style={{ width: `${Math.round(barFraction * 100)}%` }}
        />
      </Box>
    </VStack>
  );
}

export function ContextViewBody({
  data,
}: {
  data: CoPilotContextViewDebugResponse;
}) {
  const t = useTranslations('chat.copilot.agentContext');

  return (
    <VStack
      flex="1"
      alignItems="stretch"
      gap={2}
      px={4}
      py={3}
      overflow="auto"
      minH={0}
    >
      <MetaDefault color="fg.muted" fontFamily="mono">
        {t('viewStats', {
          checkpointCount: data.checkpointMessageCount,
          trigger: formatTokenCount(data.triggerTokens),
          viewCount: data.viewMessageCount,
          window: formatTokenCount(data.contextWindowTokens),
        })}
      </MetaDefault>
      {data.compactionEntries.length > 0 && (
        <>
          <MetaDefault color="fg.muted">{t('compactionHistory')}</MetaDefault>
          {data.compactionEntries.map((entry) => (
            <CompactionEntryRow
              key={entry.id}
              entry={entry}
              compactionBadgeLabel={t('compactionBadge')}
              tokenRangeLabel={t('tokenRangeShort', {
                after: formatTokenCount(entry.tokensAfter),
                before: formatTokenCount(entry.tokensBefore),
              })}
            />
          ))}
          <MetaDefault color="fg.muted" mt={1}>
            {t('currentModelView')}
          </MetaDefault>
        </>
      )}
      {data.messages.length === 0 ? (
        <BodyDefault color="fg.muted">{t('noMessagesYet')}</BodyDefault>
      ) : (
        data.messages.map((message) => (
          <ContextViewMessageRow
            key={`${message.index}-${message.id ?? 'no-id'}`}
            message={message}
            summaryBadgeLabel={t('summaryBadge')}
            tokenCountLabel={t('tokenCountShort', {
              tokens: formatTokenCount(message.estimatedTokens),
            })}
          />
        ))
      )}
    </VStack>
  );
}
